import React, { useCallback, useEffect, useState } from 'react'
import {
  Grid, TextField, MenuItem, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  makeStyles,
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AppButton from 'components/Button'
import PageTitle from 'components/PageTitle'
import { getDadosUsuario } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'
import { anosBase } from 'pages/titulos-lbc/titulos-lbc.helpers'
import { getResgates, excluirResgate, devolverSaldoResgate } from 'store/modules/api/resgates-lbc/resgates-lbc.api'
import { getEmpresas } from 'store/modules/api/titulos-lbc/titulos-lbc.api'
import Lista from './components/lista'
import DevolverSaldoModal from './components/devolver-saldo-modal'

const anos = anosBase()

// Filtro fica sobre o cover escuro do banner: texto/label/underline em branco (igual ao título da página)
const useStyles = makeStyles({
  filtroClaro: {
    '& label, & label.Mui-focused': { color: '#fff' },
    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiSelect-icon': { color: '#fff' },
    '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#fff' },
    '& .MuiInput-underline:after': { borderBottomColor: '#fff' },
  },
})

export const ResgatesLbc = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const usuario = useSelector(getDadosUsuario)
  const podeEditar = usuario?.tipoUsuario?.id !== TIPO_USUARIO.CLIENTE.OBSERVADOR_CREDITO
  const [resgates, setResgates] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useState({ anoBase: '', empresaDestinatariaId: '' })
  const [erro, setErro] = useState('')
  const [aExcluir, setAExcluir] = useState(null)
  const [aDevolver, setADevolver] = useState(null)

  const carregar = useCallback(async () => {
    setLoading(true)
    try { setResgates(await getResgates(filtros)) } catch (e) { setErro(e.message) } finally { setLoading(false) }
  }, [filtros])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => { getEmpresas().then(setEmpresas).catch((e) => setErro(e.message)) }, [])

  const confirmarExclusao = async () => {
    try {
      await excluirResgate(aExcluir.id)
      setAExcluir(null)
      await carregar()
    } catch (e) { setErro(e.message) }
  }

  const confirmarDevolucao = async (quantidade) => {
    try {
      await devolverSaldoResgate(aDevolver.id, quantidade)
      setADevolver(null)
      await carregar()
    } catch (e) { setErro(e.message) }
  }

  return (
    <>
      <PageTitle title={t('resgatesLbc.titulo')} />

      <Grid container spacing={2} style={{ marginTop: 4, marginBottom: 12 }}>
        <Grid item xs={6} sm={3}>
          <TextField
            select fullWidth size="small" className={classes.filtroClaro} label={t('resgatesLbc.anoBase')}
            value={filtros.anoBase} onChange={(e) => setFiltros((f) => ({ ...f, anoBase: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('resgatesLbc.todos')}</MenuItem>
            {anos.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            select fullWidth size="small" className={classes.filtroClaro} label={t('resgatesLbc.empresa')}
            value={filtros.empresaDestinatariaId}
            onChange={(e) => setFiltros((f) => ({ ...f, empresaDestinatariaId: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('resgatesLbc.todos')}</MenuItem>
            {empresas.map((em) => <MenuItem key={em.id} value={em.id}>{em.nome}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      {loading ? <CircularProgress /> : <Lista resgates={resgates} onExcluir={setAExcluir} onDevolver={setADevolver} podeEditar={podeEditar} />}

      {aDevolver && (
        <DevolverSaldoModal
          resgate={aDevolver}
          onFechar={() => setADevolver(null)}
          onConfirmar={confirmarDevolucao}
        />
      )}

      <Dialog open={!!aExcluir} onClose={() => setAExcluir(null)}>
        <DialogTitle>{t('resgatesLbc.excluirTitulo')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('resgatesLbc.excluirAviso')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAExcluir(null)}>{t('inputs.cancelar')}</Button>
          <AppButton variant="danger" onClick={confirmarExclusao}>{t('resgatesLbc.confirmarExclusao')}</AppButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!erro} autoHideDuration={6000} onClose={() => setErro('')} message={erro} />
    </>
  )
}

export default ResgatesLbc
